// @flow

import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import classnames from 'classnames';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import InputField from '../../../../components/FormControls/InputField';
import StandardButton from '../../../../components/Buttons/Standard';
import Label from './Label';
import Description from './Description';
import InsuranceCard from './InsuranceCard';
import OrganizationDisplay from './OrganizationDisplay';
import IdentificationTypeSelector from './IdentificationTypeSelector';
import OrganizationSelector from './OrganizationSelector';
import {
  formatIdentification,
  getIdentificationValidationRules,
  getPatternExample,
  getFirstValidationError,
} from './utils';
import useSelfEnrollment from '../../../../hooks/useSelfEnrollment';

import styles from './styles.module.scss';

type Props = {
  onSubmit: (input: Object) => Promise<void>,
};

type IdentifierConfig = {
  identifierId?: string,
  pattern: ?string,
  cardUrl: ?string,
  validations: Array<Object>,
};

const formatDate = (dateString) => {
  let formattedDate = dateString.replace(/\D/g, '');
  if (formattedDate.length > 0) {
    formattedDate = formattedDate.match(/(\d{1,2})(\d{0,2})(\d{0,4})/);
    if (formattedDate[1]) {
      formattedDate =
        formattedDate[1] +
        (formattedDate[2] ? `/${formattedDate[2]}` : '') +
        (formattedDate[3] ? `/${formattedDate[3]}` : '');
    }
  }
  return formattedDate;
};

const getSingleIdentifierDisplayName = (entityData) => {
  if (entityData?.selfEnrollment?.selfEnrollmentPatientIdentifiers?.length === 1) {
    return (
      entityData.identifierTypes.find(
        (identifierType) =>
          identifierType.id === entityData.selfEnrollment.selfEnrollmentPatientIdentifiers[0].identifierId,
      ).displayName || ''
    );
  }
  return '';
};

const Form = ({ onSubmit }: Props) => {
  const { formatMessage } = useIntl();
  const { entityData, formData, setFormData, isOrganizationDefined } = useSelfEnrollment();
  const singleIdentifierAvailable = entityData?.selfEnrollment?.selfEnrollmentPatientIdentifiers?.length === 1;

  const [identifierConfig, setIdentifierConfig] = useState<IdentifierConfig>({
    pattern: null,
    cardUrl: null,
    validations: [],
  });

  const {
    watch,
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    defaultValues: {
      ...formData,
      dateOfBirth:
        formData.dateOfBirth?.length > 0 ? moment(formData.dateOfBirth, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
    },
    mode: 'all',
  });

  const entitySuffix = watch('entitySuffix');
  const identificationType = watch('identificationType');

  useEffect(() => {
    setFormData((oldData) => ({ ...oldData, entitySuffix }));
  }, [entitySuffix, setFormData]);

  useEffect(() => {
    if (isOrganizationDefined && formData.entitySuffix && entitySuffix !== formData.entitySuffix) {
      setValue('entitySuffix', formData.entitySuffix);
    }
  }, [isOrganizationDefined, formData.entitySuffix, entitySuffix, setValue]);

  useEffect(() => {
    const identifier =
      entityData?.selfEnrollment?.selfEnrollmentPatientIdentifiers &&
      entityData.selfEnrollment.selfEnrollmentPatientIdentifiers[0]?.identifierId;
    if (singleIdentifierAvailable && identifier) {
      setValue('identificationType', identifier);
    }
  }, [singleIdentifierAvailable, entityData, entityData?.selfEnrollment, entitySuffix, setValue]);

  useEffect(() => {
    if (identificationType && entityData?.selfEnrollment?.selfEnrollmentPatientIdentifiers) {
      const selectedType = entityData.selfEnrollment.selfEnrollmentPatientIdentifiers.find(
        (identifier) => identifier.identifierId === identificationType,
      );
      if (selectedType) {
        const config = {
          identifierId: selectedType.identifierId,
          pattern: selectedType.pattern,
          cardUrl: selectedType.cardUrl || entityData?.selfEnrollment?.selfEnrollmentInsuranceCard || '',
          validations: selectedType.validations || [],
        };

        setIdentifierConfig(config);
      }
    } else {
      setIdentifierConfig({
        pattern: null,
        cardUrl: null,
        validations: [],
      });
    }
  }, [identificationType, entityData?.selfEnrollment]);

  const handleOnSubmit = handleSubmit(async (data) => {
    const { dateOfBirth } = data;
    const formattedDate = moment(dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DD');

    setFormData({ ...data, dateOfBirth: formattedDate });
    await onSubmit({
      ...data,
      dateOfBirth: formattedDate,
    });
  });

  const handleDateChange = (event, field) => {
    const formattedDate = formatDate(event.target.value);
    field.onChange(formattedDate);
  };

  const handleIdentificationChange = (event, field) => {
    if (identifierConfig.pattern) {
      const formattedIdentification = formatIdentification(event.target.value, field.value, identifierConfig.pattern);
      field.onChange(formattedIdentification);
    } else {
      field.onChange(event.target.value);
    }
  };

  const otherFieldsDisabled = !entityData;

  return (
    <form className={styles.form} onSubmit={handleOnSubmit}>
      <div className={styles.formContainer}>
        <div className={styles.inputContainer}>
          <Label
            required={!isOrganizationDefined}
            text={
              entityData?.selfEnrollment?.selfEnrollmentHealthPlanLabel ||
              formatMessage({ id: 'app.self-enroll.identify.form.insurance-title' })
            }
          />
          {isOrganizationDefined && (
            <>
              <OrganizationDisplay />
              <Controller
                name="entitySuffix"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
            </>
          )}
          {!isOrganizationDefined && (
            <Controller
              name="entitySuffix"
              control={control}
              rules={{ required: 'app.self-enroll.identify.form.field-required' }}
              render={({ value, onChange }) => <OrganizationSelector value={value} onChange={onChange} />}
            />
          )}
        </div>

        <div className={styles.inputContainer}>
          <Label text={formatMessage({ id: 'app.self-enroll.identify.form.first-name-title' })} required />
          <Controller
            name="firstName"
            control={control}
            rules={{ required: 'app.self-enroll.identify.form.field-required', minLength: 1 }}
            render={(field) => (
              <InputField
                input={field}
                meta={{ touched: !!errors.firstName, error: errors?.firstName?.message ?? null }}
                disabled={otherFieldsDisabled}
                placeholder={formatMessage({ id: 'app.self-enroll.identify.form.first-name-placeholder' })}
              />
            )}
          />
        </div>

        <div className={styles.inputContainer}>
          <Label text={formatMessage({ id: 'app.self-enroll.identify.form.last-name-title' })} required />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: 'app.self-enroll.identify.form.field-required', minLength: 1 }}
            render={(field) => (
              <InputField
                input={field}
                meta={{ touched: !!errors.lastName, error: errors?.lastName?.message ?? null }}
                disabled={otherFieldsDisabled}
                placeholder={formatMessage({ id: 'app.self-enroll.identify.form.last-name-placeholder' })}
              />
            )}
          />
        </div>

        <div className={styles.inputContainer}>
          <Label text={formatMessage({ id: 'app.self-enroll.identify.form.dob-title' })} required />
          <Controller
            name="dateOfBirth"
            control={control}
            rules={{
              required: 'app.self-enroll.identify.form.field-required',
              validate: (value) => {
                if (value.length !== 10) return 'app.self-enroll.identify.form.valid-date';
                const date = moment(value, 'MM/DD/YYYY');
                if (!moment(date).isValid()) {
                  return 'app.self-enroll.identify.form.valid-date';
                }

                const now = moment.now();
                if (!date.isSameOrBefore(now)) {
                  return 'app.self-enroll.identify.form.future-date';
                }

                return true;
              },
            }}
            render={(field) => (
              <InputField
                input={field}
                meta={{ touched: !!errors.dateOfBirth, error: errors?.dateOfBirth?.message ?? null }}
                placeholder="MM/DD/YYYY"
                onChange={(e) => handleDateChange(e, field)}
                disabled={otherFieldsDisabled}
              />
            )}
          />
        </div>

        <div className={styles.inputContainer}>
          <Label
            text={
              singleIdentifierAvailable
                ? getSingleIdentifierDisplayName(entityData)
                : formatMessage({ id: 'app.self-enroll.identify.form.identification-type-title' })
            }
            required
          />
          <Description text={formatMessage({ id: 'app.self-enroll.identify.form.memberid-sub-title' })} />

          <InsuranceCard src={identifierConfig.cardUrl} />
          {singleIdentifierAvailable && (
            <Controller
              name="identificationType"
              control={control}
              defaultValue={entityData.selfEnrollment.selfEnrollmentPatientIdentifiers[0].identifierId}
              render={({ field }) => (
                <input
                  type="hidden"
                  {...field}
                  value={entityData.selfEnrollment.selfEnrollmentPatientIdentifiers[0].identifierId}
                />
              )}
            />
          )}
          {!singleIdentifierAvailable && (
            <Controller
              name="identificationType"
              control={control}
              rules={{
                required: formatMessage({ id: 'app.self-enroll.identify.form.field-required' }),
              }}
              render={({ value, onChange }) => <IdentificationTypeSelector value={value} onChange={onChange} />}
            />
          )}
          <Controller
            name="identification"
            control={control}
            rules={{
              required: formatMessage({ id: 'app.self-enroll.identify.form.field-required' }),
              validate: (value) => {
                const rules = getIdentificationValidationRules(identifierConfig);
                const error = getFirstValidationError(value || '', rules);
                return error || true;
              },
            }}
            render={(field) => (
              <>
                <InputField
                  input={field}
                  meta={{ touched: !!errors.identification, error: errors?.identification?.message ?? null }}
                  disabled={otherFieldsDisabled}
                  onChange={(e) => handleIdentificationChange(e, field)}
                  placeholder={
                    identifierConfig.pattern ? getPatternExample(identifierConfig.pattern) : 'Enter identification'
                  }
                />
              </>
            )}
          />
        </div>

        <input type="submit" style={{ display: 'none' }} />
        <StandardButton
          disabled={!isValid}
          extraRootClasses={classnames(styles.button, {
            [styles.buttonDisabled]: !isValid,
          })}
          isPill
          variant="primary"
          onClick={handleOnSubmit}
        >
          <FormattedMessage id="app.self-enroll.identify.form.submit" />
        </StandardButton>
      </div>
    </form>
  );
};

export default Form;
